import _isFunction from "lodash/isFunction";
import _isString from "lodash/isString";
import { normalize } from "normalizr";

export const PROGRESS = Object.seal({
    initial: "initial",
    loading: "loading",
    success: "success",
    error: "error"
});

export function shouldLoadOnMount(progress) {
    return progress === PROGRESS.initial || progress === PROGRESS.error;
}

export function shouldLoadOnUpdate(progress) {
    return progress === PROGRESS.initial;
}

export function isLoading(progress) {
    return progress === PROGRESS.initial || progress === PROGRESS.loading;
}

export function isLoaded(progress) {
    return !isLoading(progress);
}

async function parseResponseData(response) {
    try {
        return await response.json();
    } catch (e) {
        console.error("Invalid JSON in HTTP response", e, response);
        return null;
    }
}

/**
 * Creates a redux-thunk action creator that issues an asynchronous HTTP request and dispatches Redux actions before and
 * after the request completes. The returned function has the signature `async (...args): ReduxThunkAction`, where
 * `args` are "Action Arguments" that may be customized for each HTTP request and `ReduxThunkAction` is a function with
 * the signature `(dispatch) => Promise<SuccessAction|ErrorAction>`.
 *
 * **Specifying Request Details**
 *
 * `asyncHttpAction` uses the [Fetch API](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API) to handle the HTTP
 * request.
 *
 * The request URL is determined by the `url` parameter, which can be a string or a function with the signature.
 *     * If it's a string, that value is used directly.
 *     * If it's a function, the signature is assumed to be `(getState, ...args): string`, where `getState` is a
 *     function that returns the application state from the Redux store and `args` are the Action Arguments. When
 *     invoked, the function must return the request URL.
 *
 * By default, Fetch will issue a GET request. You can customize the Fetch request details by using the `request`
 * parameter. `request` can be a Fetch request object or a function with the signature
 * `(getState, ...args): FetchRequest`, where `getState` is a function that returns the contents of the Redux store and
 * `args` are the Action Arguments.
 *
 * In addition, `asyncHttpAction` will automatically add a few HTTP headers to your request object:
 *     * If there is an API key stored in the Redux store's `authToken` field, `asyncHttpAction` will add an
 *     `Authorization: Bearer <apiKey>` header.
 *     * If the request has a non-`FormData` value for its `body` field, but no `Content-type` header, then
 *     `Content-Type: application/json` will be added.
 *     * If the request does not have an `Accept` header, `Accept: application/json` will be added.
 *
 * **Processing Response Data**
 *
 * If the HTTP request completes successfully, `asyncHttpRequest` attempts to parse the response as JSON data. If
 * parsing fails, it will preserve the response as a string.
 *
 * After parsing, `asyncHttpAction` uses 3 optional parameters (in order) to process the response data for a successful
 * HTTP request: `paginated`, `processResponse`, and `schema`.
 *
 * 1. If `paginated` is `true`, then the response records will be separated from the page information. Subsequent
 * operations will only work on the response records. The page information will be added to the Redux SUCCESS action's
 * payload.
 * 2. If provided, `processResponse` must be a function with the signature `(data: string|object): any`. The parsed
 * response data will be passed to this function. Subsequent operations will use the function's return value instead of
 * the HTTP response data.
 * 3. If `schema` is given, it must be a `normalizr` schema entity. The data will be normalized using this schema and
 * subsequent operations will use the `{result, entities}` object instead of the HTTP response data.
 *
 * **Redux Actions**
 *
 * The action function also has 3 properties named `REQUEST`, `SUCCESS`, and `ERROR`. Those properties store the types
 * of the Redux actions for use in Redux reducers.
 *
 * Each of the `REQUEST`, `SUCCESS`, and `ERROR` actions have a `meta` property that identifies the action as an API
 * request and indicates its progress using the `PROGRESS` enum. They also store an array containing the Action
 * Arguments in their `payload` in a property named `args`. If your action accepts sensitive arguments and you don't
 * want them to appear in the Redux actions, you can use the optional `argsForPayload` parameter to remove them.
 * `argsForPayload` is a function with the signature `(...args) => any`, where `args` are the Action Arguments.
 *
 * The `ERROR` action has a property named `error`. If the server returns an error response, `error` that stores the
 * HTTP error code in the `status` field, the HTTP status text in the `message` field, and the HTTP response body in the
 * `details` field. If the action function throws an exception for some other reason, `status` will be `null`, `message`
 * will contain the Exception's message, and `details` will contain the Exception object.
 *
 * The `SUCCESS` action stores the processed HTTP response in its payload in a field named `data`. If the response is
 * paginated, the page info will be stored in the payload in a field named `pagination`.
 *
 * @param {string} actionPrefix A prefix for the generated Redux action types. "/REQUEST", "/SUCCESS", and "/ERROR" will
 * be appended to this value.
 * @param {string | function(getState, ...args): string} url Specifies the request URL.
 * @param {object | function(getState, ...args): object} request Specifies the Fetch API request object.
 * @param {function(...args) | null} argsForPayload A function that can be used to customize the actions' `payload.args`
 * value.
 * @param {boolean} paginated Determines whether the response data is paginated. Page info will be extracted from the
 * response body prior to calling `processResponse`.
 * @param {string} paginatedItems Determines the name of the field that contains the paginated items in a paginated
 * response. This value is only used if `paginated` is true.
 * @param {function(responseBody): any | null} processResponse A function that can be used to pre-process the response
 * data before normalizing it.
 * @param {schema.Entity | null} schema The response body will be normalized using this schema.
 * @returns {function(...args): ReduxThunkAction} A function that returns a redux-thunk action function when invoked.
 */
export function asyncHttpAction(
    actionPrefix,
    url,
    {
        request = {},
        argsForPayload = null,
        processResponse = null,
        paginated = false,
        paginatedItems = "rows",
        schema = null
    } = {
        request: {},
        argsForPayload: null,
        processResponse: null,
        paginated: false,
        paginatedItems: "rows",
        schema: null
    }
) {
    // These are the Redux action types
    const SUCCESS = `${actionPrefix}/SUCCESS`;
    const REQUEST = `${actionPrefix}/REQUEST`;
    const ERROR = `${actionPrefix}/ERROR`;

    // These are factories for the Redux actions issued by the thunk
    function requestAction(url, request, args) {
        return {
            type: REQUEST,
            payload: {
                args
            },
            meta: {
                isApi: true,
                progress: PROGRESS.loading,
                url,
                request
            }
        };
    }

    function successAction(url, request, args, data, pagination) {
        return {
            type: SUCCESS,
            payload: {
                args,
                data,
                pagination
            },
            meta: {
                isApi: true,
                progress: PROGRESS.success,
                url,
                request
            }
        };
    }

    function errorAction(url, request, args, status, message, details) {
        return {
            type: ERROR,
            payload: {
                args
            },
            error: {
                status,
                message,
                details
            },
            meta: {
                isApi: true,
                progress: PROGRESS.error,
                url,
                request
            }
        };
    }

    // This is the action creator function. It accepts the Action Arguments and returns a redux-thunk action function
    // that can be dispatch()ed
    const action = function(...args) {
        const payloadArgs = argsForPayload ? argsForPayload(args) : args;

        return async function(dispatch, getState) {
            let fetchUrl;
            let fetchRequest;

            // The try/catch sets the action. We dispatch it outside of the try/catch so that any errors thrown while
            // processing a SUCCESS action won't cause an ERROR action to get dispatched. This will cause unhandled
            // rejection exceptions if you aren't careful, but it's correct.
            let action = null;
            try {
                fetchUrl = _isFunction(url) ? url(getState, ...args) : url;
                fetchRequest = makeFetchRequest(
                    getState,
                    _isFunction(request) ? request(getState, ...args) : request
                );

                dispatch(requestAction(fetchUrl, fetchRequest, payloadArgs));

                const response = await fetch(fetchUrl, fetchRequest);
                const data = await parseResponseData(response);

                if (!response.ok) {
                    return dispatch(
                        errorAction(
                            fetchUrl,
                            fetchRequest,
                            payloadArgs,
                            response.status,
                            response.statusText,
                            data
                        )
                    );
                }

                const { [paginatedItems]: rows, ...pagination } = paginated
                    ? data
                    : { rows: data };
                const processed = processResponse
                    ? processResponse(rows, ...args)
                    : rows;
                const normalized = schema
                    ? normalize(processed, schema)
                    : processed;
                action = successAction(
                    fetchUrl,
                    fetchRequest,
                    payloadArgs,
                    normalized,
                    pagination
                );
            } catch (err) {
                action = errorAction(
                    fetchUrl,
                    fetchRequest,
                    payloadArgs,
                    null,
                    err.message,
                    err
                );
            }

            return dispatch(action);
        };
    };

    // Store the action types in fields on the action creator function. You can use these in a Redux reducer.
    action.REQUEST = REQUEST;
    action.SUCCESS = SUCCESS;
    action.ERROR = ERROR;

    return action;
}

export function makeFetchRequest(getState, request = {}) {
    const sessionState = getState().session;

    const headers = new Headers(request.headers);
    if (sessionState.authToken) {
        headers.set("Authorization", `Bearer ${sessionState.authToken}`);
    }

    let body = request.body;
    if (body) {
        if (!(body instanceof FormData)) {
            if (!_isString(body)) {
                body = JSON.stringify(body);
            }
            if (!headers.has("Content-Type")) {
                headers.set("Content-Type", "application/json");
            }
        }
    }

    if (!headers.has("Accept")) {
        headers.set("Accept", "application/json");
    }

    return {
        ...request,
        headers,
        body
    };
}



// WEBPACK FOOTER //
// ./src/modules/asyncHttpAction.js